// SCREENS-S1 (decision §7.5): hand a taken-over screen back to its agent, with an optional
// one-line note the agent reads. A quick action, so a small modal over the workspace.
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  toast,
} from "@companyos/ui";
import { useMutation } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import type { Api } from "../../api/client";
import { problemText } from "../settings/problem-text";
import type { Lease } from "./screens-queries";

/** What the lease is on: the account's handle, "Desktop", or "No login". */
export function leaseWhat(l: Pick<Lease, "account" | "kind">): string {
  return l.account ? l.account.handle : l.kind === "desktop" ? "Desktop" : "No login";
}

export function HandBackDialog({
  api,
  co,
  leaseId,
  agent,
  onDone,
  onCancel,
}: {
  api: Api;
  co: string;
  leaseId: string;
  /** The agent it goes back to (SCREEN-HANDOFF: "Hand back to <agent>"; it is woken to carry on). */
  agent?: string | null;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [note, setNote] = useState("");
  const title = agent ? `Hand back to ${agent}` : "Hand back";
  const send = useMutation({
    mutationFn: () =>
      api.client.screens.handBack({
        params: { co, id: leaseId },
        body: note.trim() ? { note: note.trim() } : {},
      }),
    onSuccess: () => {
      toast.add({ title: agent ? `Handed back to ${agent}` : "Handed back", type: "success" });
      onDone();
    },
    onError: (e) => toast.add({ title: problemText(e, "Could not hand it back."), type: "error" }),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    send.mutate();
  };
  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor="hand-back-note">Note for the agent</FieldLabel>
            <Input
              id="hand-back-note"
              maxLength={280}
              placeholder="Optional, never a password"
              autoComplete="off"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <FieldDescription>{note.length}/280</FieldDescription>
          </Field>
          <DialogFooter>
            <DialogClose render={<Button type="button" variant="outline" />}>Cancel</DialogClose>
            <Button type="submit" disabled={send.isPending}>
              {title}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
